
import { socket } from "@/services/game.service";
import { Card } from "@/types";
import {
  GameCardTypes,
  getValidTargets,
  TargetSelect,
  TargetTypes,
  gameConfig,
  GameStage,
  GameCard,
  AddPlayer,
} from "monkeybattle-shared";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from "vue";
import { useClientGame, serverCallback } from "../../game/gameClient";
import { useAiGame } from "../../game/aiGameClient";
import CardComponent from "./Card.vue";
import Arrow from "./Arrow.vue";
import Monkey from "./Monkey.vue";
import { userStore } from "@/store";
import { deckService } from "@/services/deck.service";
import { useToast } from "vue-toastification";
import { staticPlayerUser } from "@/game/defaultDeck";

type TargetOrCard = TargetTypes.EnemyMonkey | (GameCard & { uid: string });

type ElementCoords = {
  x?: number;
  y?: number;
};

enum InfoPanel {
  HandCard = "HandCard",
  BoardCard = "BoardCard",
  Default = "Default",
}

const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export default defineComponent({
  name: "Game",
  props: {
    ai: {
      type: Boolean,
    },
    deckId: {
      type: Number,
    },
    static: {
      type: Boolean,
    },
  },
  components: {
    Card: CardComponent,
    Arrow,
    Monkey,
  },
  emits: ["notFound"],

  async setup(props, { emit }) {
    const toast = useToast();
    let selectedGameClient;

    // watch cannot be called after await
    // stop handles have to be returned and called manually to close watchers
    const stopHandles = [] as (() => void)[];

    onBeforeUnmount(() => {
      if (!game) return;

      if (game.state.stage !== GameStage.Ended) {
        const answer = window.confirm(
          "Are you sure you want to disconnect from the game?"
        );
        if (!answer) return false;
      }

      destroyGame();

      if (!props.ai) socket.disconnect();

      // close watchers
      stopHandles.forEach((h) => h());
    });

    // load game
    if (props.ai) {
      if (typeof props.deckId !== "number")
        throw new Error("Deck index not specified");

      let playerParam: AddPlayer;

      if (props.static) {
        playerParam = staticPlayerUser;
      } else {
        playerParam = {
          user: userStore.getters.user(),
          deck: await deckService.getDeckWithCards(props.deckId),
        };
      }

      selectedGameClient = await useAiGame(playerParam);
    } else {
      try {
        selectedGameClient = await useClientGame();
      } catch (error) {
        emit("notFound");
        throw new Error("Game not found");
      }
    }

    // load game data from the selected client
    const {
      game,
      attack,
      eventEmitter,
      reshuffle,
      acceptPickedCards,
      doneTurn,
      playCreature,
      playSpell,
      ready,
      destroyGame,
    } = selectedGameClient;

    let timerAnimation: Animation;
    let timerSetup = false;
    const timer = ref<HTMLElement>();
    const timerHandler = () => {
      if (timer.value) {
        timerAnimation = timer.value.animate(
          [{ width: "100%" }, { width: "0%" }],
          {
            duration: gameConfig.turnTimeout,
          }
        );
      } else {
        watch(
          () => timer.value,
          (newVal) => {
            if (newVal && !timerSetup) {
              timerHandler();
              timerSetup = true;
            }
          }
        );
      }
    };

    // start timer when stage changes to main game
    eventEmitter.on("roundStart", (round: number) => {
      if (round === 0) timerHandler();
    });
    // reset timer on each turn change
    stopHandles.push(
      watch(
        () => game.state.turn,
        () => timerHandler()
      )
    );

    const arrowCoords = ref<
      {
        from: { x: number; y: number };
        to: { x: number; y: number };
      }[]
    >([]);

    const getTargetBoundsFromCardUid = (
      cardUid: string,
      target: TargetOrCard
    ) => {
      if (target === TargetTypes.EnemyMonkey) {
        let t;
        if (game.state.player.board.find((c) => c.uid === cardUid))
          t = document.getElementById("opponent-monkey");
        else t = document.getElementById("player-monkey");
        if (!t) throw new Error("Opponent monkey element not found");
        return t.getBoundingClientRect();
      } else {
        let t = document.getElementById(`card-${target.uid}`);
        if (!t) throw new Error("Attacked card not found");
        return t.getBoundingClientRect();
      }
    };

    // animations and timer adjust
    eventEmitter.on(
      "spellPlayed",
      async (data: { user: string; card: Card; target?: TargetOrCard }) => {
        if (!timerAnimation) return;
        timerAnimation.pause();

        // show spell on board
        if (game.state.player.user.waxAddress === data.user) {
          game.state.player.board.push(data.card);
        } else game.state.opponent.board.push(data.card);

        // wait for DOM update
        nextTick(async () => {
          if (data.target) {
            const from = document
              .getElementById(`card-${data.card.uid}`)
              ?.getBoundingClientRect();
            if (!from) throw new Error("Element not found");

            const to = getTargetBoundsFromCardUid(data.card.uid, data.target);

            arrowCoords.value.push({
              from: {
                x: from.x + from.width / 2,
                y: from.y + from.height / 2,
              },
              to: {
                x: to.x + to.width / 2,
                y: to.y + to.height / 2,
              },
            });
          }

          await sleep(gameConfig.playSpellDelay);

          arrowCoords.value.shift();

          game.state.player.board = game.state.player.board.filter(
            (c) => c.uid !== data.card.uid
          );
          game.state.opponent.board = game.state.opponent.board.filter(
            (c) => c.uid !== data.card.uid
          );
          timerAnimation.play();
        });
      }
    );

    eventEmitter.on(
      "creaturePlayed",
      async (data: { uid: string; target?: TargetOrCard }) => {
        if (!timerAnimation) return;
        timerAnimation.pause();

        nextTick(async () => {
          if (data.target) {
            const from = document
              .getElementById(`card-${data.uid}`)
              ?.getBoundingClientRect();
            if (!from) throw new Error("Element not found");

            const to = getTargetBoundsFromCardUid(data.uid, data.target);

            arrowCoords.value.push({
              from: {
                x: from.x + from.width / 2,
                y: from.y + from.height / 2,
              },
              to: {
                x: to.x + to.width / 2,
                y: to.y + to.height / 2,
              },
            });
          }

          await sleep(gameConfig.playCreatureDelay);

          arrowCoords.value.shift();
          timerAnimation.play();
        });
      }
    );

    // attack animation
    eventEmitter.on("creatureAttack", async (data) => {
      if (!timerAnimation) return;
      timerAnimation.pause();

      const attackingCard = document.getElementById(
        `card-${data.attackingUid}`
      );
      if (!attackingCard) throw new Error("Attacking card not found");
      const attackingCardBounds = attackingCard.getBoundingClientRect();

      let targetBounds = getTargetBoundsFromCardUid(
        data.attackingUid,
        data.attacked
      );

      let translateY = targetBounds.y - attackingCardBounds.y;
      if (translateY > 0) translateY += targetBounds.height / 2;
      else translateY -= targetBounds.height / 2;

      const translateX = targetBounds.x - attackingCardBounds.x;
      const angle = Math.atan2(translateY, translateX) + Math.PI / 2;

      attackingCard.animate(
        [
          { transform: "translate(0, 0)" },
          {
            transform: `translate(${translateX}px, ${translateY}px) rotate(${angle}rad)`,
          },
          { transform: "translate(0, 0)" },
        ],
        { duration: 500, easing: "ease-in-out" }
      );

      await sleep(gameConfig.attackDelay);
      timerAnimation.play();
    });

    const playCard = (card: Card) => {
      if (game.state.turn !== game.state.playerTurn)
        toast.error("Opponent turn, cannot play card!");
      else if (game.state.player.potassium < card.potassium)
        toast.error("Not enough potassium!");
      else if (!selectedHandCard.value) {
        if (
          card.type === GameCardTypes.CREATURE &&
          game.state.player.board.length < 6
        ) {
          if (
            card.instant &&
            card.instant.targets?.select === TargetSelect.ByPlayer
          ) {
            if (
              getValidTargets(
                card,
                card.instant.targets,
                game.state.player,
                game.state.opponent
              ).length > 0
            ) {
              card.handSelected = true;
            } else {
              playCreature(card.uid);
            }
          } else playCreature(card.uid);
        } else if (card.type === GameCardTypes.SPELL) {
          if (card.spell?.targets?.select === TargetSelect.ByPlayer) {
            if (
              getValidTargets(
                card,
                card.spell.targets,
                game.state.player,
                game.state.opponent
              ).length > 0
            ) {
              card.handSelected = true;
            } else {
              toast.error("Spell has no valid targets");
            }
          } else playSpell(card.uid);
        }
      } else {
        toast.error("Cannot play card!");
      }
    };

    const selectedHandCard = computed<Card | undefined>(() =>
      game.state.player.hand?.find((c) => c.handSelected)
    );
    const handCardSelectedCoords = reactive<ElementCoords>({});
    const setHandSelectedElement = (comp: { $el: HTMLElement }, card: Card) => {
      if (card.handSelected && comp) {
        const rect = comp.$el.getBoundingClientRect();
        handCardSelectedCoords.x = rect.left + rect.width / 2;
        handCardSelectedCoords.y = rect.top + rect.height / 2;
      }
    };

    const selectedBoardCard = computed<Card | undefined>(() =>
      game.state.player.board.find((c) => c.boardSelected)
    );
    const boardCardSelectedCoords = reactive<ElementCoords>({});
    const setBoardSelectedElement = (
      comp: { $el: HTMLElement },
      card: Card
    ) => {
      if (card.boardSelected && comp) {
        const rect = comp.$el.getBoundingClientRect();
        boardCardSelectedCoords.x = rect.left + rect.width / 2;
        boardCardSelectedCoords.y = rect.top + rect.height / 2;
      }
    };

    const boardCardPressed = (card: Card) => {
      if (
        game.state.turn === game.state.playerTurn &&
        !selectedBoardCard.value &&
        !selectedHandCard.value &&
        card.enabled &&
        !card.attackDisabled
      )
        card.boardSelected = true;
    };
    window.addEventListener("mouseup", () => {
      if (selectedBoardCard.value)
        selectedBoardCard.value.boardSelected = false;
      if (selectedHandCard.value) selectedHandCard.value.handSelected = false;
    });

    const targetClicked = (
      target: Card | TargetTypes.EnemyMonkey,
      type: TargetTypes
    ) => {
      const handCb: serverCallback = (res) => {
        if (selectedHandCard.value) selectedHandCard.value.handSelected = false;
        if (res.error) {
          toast.error(res.error);
        }
      };

      const boardCb: serverCallback = (res) => {
        if (res.error) {
          toast.error(res.error);
        } else console.log("Attack successful");
      };

      if (selectedHandCard.value) {
        if (selectedHandCard.value.instant) {
          if (selectedHandCard.value.instant.targets?.type.includes(type)) {
            playCreature(
              selectedHandCard.value.uid,
              target === TargetTypes.EnemyMonkey ? target : target.uid,
              handCb
            );
          } else {
            toast.error("Invalid target selected");
          }
        } else if (selectedHandCard.value.spell) {
          if (selectedHandCard.value.spell.targets?.type.includes(type)) {
            playSpell(
              selectedHandCard.value.uid,
              target === TargetTypes.EnemyMonkey ? target : target.uid,
              handCb
            );
          } else {
            toast.error("Invalid target selected");
          }
        }
      } else if (selectedBoardCard.value) {
        if (
          type !== TargetTypes.EnemyMonkey &&
          type !== TargetTypes.EnemyCreature
        ) {
          toast.error("Invalid target selected");
          return;
        }
        attack(
          selectedBoardCard.value.uid,
          target === TargetTypes.EnemyMonkey ? target : target.uid,
          boardCb
        );
      }
    };

    eventEmitter.on("warnIdle", () => {
      toast.error("Warning: you will be kicked for being idle");
    });

    eventEmitter.on("victory", () => {
      if (timerAnimation) timerAnimation.pause();
    });

    const infoPanelType = ref(InfoPanel.Default);
    const infoSelectedCard = ref<Card>();
    const showInfoPanel = ref(false);
    const setInfoPanel = (type: InfoPanel, card?: Card) => {
      infoPanelType.value = type;
      infoSelectedCard.value = card;
    };

    await ready();

    return {
      game,
      reshuffle,
      acceptPickedCards,
      playCard,
      doneTurn,
      selectedHandCard,
      arrowCoords,
      setHandSelectedElement,
      handCardSelectedCoords,
      selectedBoardCard,
      setBoardSelectedElement,
      boardCardSelectedCoords,
      boardCardPressed,
      targetClicked,
      timer,
      infoPanelType,
      infoSelectedCard,
      setInfoPanel,
      showInfoPanel,
      InfoPanel,
      TargetSelect,
      TargetTypes,
    };
  },
});
